"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useId, useRef, useState } from "react";
import { ZoneLink } from "@/components/layout/zone-link";
import { signOut } from "@/lib/auth/client";

type ProfileDropdownProps = {
	email: string;
	currentStreak: number;
	streakLabel: string;
	/** Weekly-milestone celebratory line ("7 hari berturut-turut 🔥") — when
	 * present, replaces the generic label+count row entirely. */
	streakMilestoneLabel?: string;
	streakUnitDays: string;
	logoutLabel: string;
	loggingOutLabel: string;
	menuLabel: string;
	locale: string;
	trackerLabel: string;
	resumeLabel: string;
	alertsLabel: string;
	isPro?: boolean;
};

export function ProfileDropdown({
	email,
	currentStreak,
	streakLabel,
	streakMilestoneLabel,
	streakUnitDays,
	logoutLabel,
	loggingOutLabel,
	menuLabel,
	locale,
	trackerLabel,
	resumeLabel,
	alertsLabel,
	isPro = false,
}: ProfileDropdownProps) {
	const router = useRouter();
	const pathname = usePathname();
	const [open, setOpen] = useState(false);
	const [loggingOut, setLoggingOut] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const menuId = useId();

	useEffect(() => {
		if (!open) return;
		function onClick(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setOpen(false);
			}
		}
		function onKey(event: KeyboardEvent) {
			if (event.key === "Escape") setOpen(false);
		}
		document.addEventListener("mousedown", onClick);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onClick);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	async function handleLogout() {
		try {
			setLoggingOut(true);
			await signOut({
				fetchOptions: {
					onSuccess: () => {
						router.push("/");
						router.refresh();
					},
				},
			});
		} catch (error) {
			console.error("Logout failed:", error);
		} finally {
			setLoggingOut(false);
		}
	}

	const initial = (email.trim().charAt(0) || "?").toUpperCase();

	return (
		<div ref={containerRef} className="relative inline-flex items-center">
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				aria-haspopup="true"
				aria-expanded={open}
				aria-controls={menuId}
				aria-label={menuLabel}
				className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border bg-primary text-sm font-bold text-primary-foreground shadow-sm transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
					isPro
						? "border-amber-400 ring-2 ring-amber-400/60 focus-visible:ring-amber-400 dark:border-amber-500 dark:ring-amber-500/50"
						: "border-border/70 focus-visible:ring-ring"
				}`}
			>
				<span aria-hidden="true">{initial}</span>
			</button>

			{open ? (
				<div
					id={menuId}
					data-testid="profile-dropdown-panel"
					className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-border/70 bg-popover p-1 shadow-lg"
				>
					<div
						className="border-b border-border/40 px-3 py-3"
						aria-hidden={false}
					>
						<p className="truncate text-xs text-muted-foreground">{email}</p>
						{isPro && (
							<span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
								Pro member
							</span>
						)}
					</div>

					<div className="flex items-center justify-between gap-2 px-3 py-2.5">
						{streakMilestoneLabel ? (
							<span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
								{streakMilestoneLabel}
							</span>
						) : (
							<>
								<span className="inline-flex items-center gap-2 text-sm text-foreground">
									<span>{streakLabel}</span>
								</span>
								<span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
									{currentStreak} {streakUnitDays}
								</span>
							</>
						)}
					</div>

					<div className="border-t border-border/40 pt-1">
						<ZoneLink
							href={`/${locale}/seeker`}
							prefetch={false}
							aria-current={
								pathname === `/${locale}/seeker` ? "page" : undefined
							}
							onClick={() => setOpen(false)}
							className="flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							Dashboard
						</ZoneLink>
						<ZoneLink
							href={`/${locale}/applications`}
							aria-current={
								pathname === `/${locale}/applications` ? "page" : undefined
							}
							onClick={() => setOpen(false)}
							className="flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{trackerLabel}
						</ZoneLink>
						<ZoneLink
							href={`/${locale}/resume`}
							aria-current={
								pathname === `/${locale}/resume` ? "page" : undefined
							}
							onClick={() => setOpen(false)}
							className="flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{resumeLabel}
						</ZoneLink>
						<ZoneLink
							href={`/${locale}/saved-searches`}
							aria-current={
								pathname === `/${locale}/saved-searches` ? "page" : undefined
							}
							onClick={() => setOpen(false)}
							className="flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{alertsLabel}
						</ZoneLink>
					</div>

					<div className="border-t border-border/40 pt-1">
						<button
							type="button"
							onClick={handleLogout}
							disabled={loggingOut}
							className="flex w-full cursor-pointer items-center rounded-xl px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50"
						>
							{loggingOut ? loggingOutLabel : logoutLabel}
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
}
